import { api } from "@/lib/api";
import { cn, ensLevelTone } from "@/lib/util";
import { Loader2, Shield } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const LEVELS = [
  { id: "basico", label: "BÁSICO", hint: "Riesgo bajo · impacto limitado" },
  { id: "medio",  label: "MEDIO",  hint: "Riesgo apreciable · afecta el desempeño" },
  { id: "alto",   label: "ALTO",   hint: "Riesgo muy alto · grave perjuicio para la Administración" },
];

/**
 * Pill toolbar to classify a client's information system per Anexo I del RD 311/2022.
 * Emits PATCH /clients/{id}/ens-level and calls onChanged so the parent refetches.
 */
export function ENSLevelSelector({ clientId, level, disabled, onChanged, appliedCount, totalCount }) {
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState(null);
  const current = optimistic || level || "basico";

  const setLevel = async (next) => {
    if (busy || next === current) return;
    setBusy(true);
    setOptimistic(next);
    try {
      await api.patch(`/clients/${clientId}/ens-level`, { level: next });
      toast.success(`Categoría ENS · ${LEVELS.find((l) => l.id === next).label}`);
      onChanged?.(next);
    } catch (e) {
      setOptimistic(null);
      toast.error(e?.response?.data?.detail || "No se pudo cambiar la categoría ENS");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="ens-level-selector">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-primary" />
          <div>
            <div className="overline">Categoría del sistema · Anexo I RD 311/2022</div>
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
              El score ENS pondera <span className="text-primary">basico=1</span> ·{" "}
              <span className="text-sev-medium">medio=2</span> ·{" "}
              <span className="text-sev-critical">alto=3</span> y filtra los controles aplicables.
            </div>
          </div>
        </div>
        {typeof appliedCount === "number" && typeof totalCount === "number" && (
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground" data-testid="ens-applicable-count">
            {appliedCount} / {totalCount} controles aplicables
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Categoría ENS">
        {LEVELS.map((l) => {
          const tone = ensLevelTone(l.id);
          const active = current === l.id;
          return (
            <button
              key={l.id}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled || busy}
              onClick={() => setLevel(l.id)}
              className={cn(
                "flex flex-col items-start px-3 py-2 border transition-colors text-left flex-1 min-w-[8rem]",
                active
                  ? cn(tone.border, tone.bg)
                  : "border-border bg-background/40 hover:border-muted-foreground",
                (disabled || busy) && "opacity-60 cursor-not-allowed"
              )}
              data-testid={`ens-level-${l.id}`}
              data-active={active}
            >
              <span className={cn("font-mono text-[10px] tracking-widest uppercase font-semibold flex items-center gap-1",
                                  active ? tone.text : "text-muted-foreground")}>
                {active && busy && <Loader2 className="w-3 h-3 animate-spin" />}
                {l.label}
              </span>
              <span className="text-[10px] text-muted-foreground mt-1 leading-tight">{l.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
